// Compliance History Panel
// Displays past inspections, compliance events, and status changes for an asset

import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Download,
  FileSpreadsheet,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Clock,
  User,
  FileX,
  Edit,
  Trash2,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Event types
const EVENT_TYPES = [
  { value: 'all', label: 'All Events' },
  { value: 'InspectionRecorded', label: 'Inspection Recorded' },
  { value: 'InspectionScheduled', label: 'Inspection Scheduled' },
  { value: 'StatusChanged', label: 'Status Changed' },
  { value: 'DueDateDeferred', label: 'Due Date Deferred' },
  { value: 'WorkItemCreated', label: 'Work Item Created' },
  { value: 'MarkedCompliant', label: 'Marked as Compliant' },
  { value: 'MarkedNonApplicable', label: 'Marked as Non-Applicable' },
  { value: 'EvidenceUploaded', label: 'Evidence Uploaded' },
  { value: 'EvidenceDeleted', label: 'Evidence Deleted' },
  { value: 'ComplianceAssigned', label: 'Compliance Assigned' },
  { value: 'ComplianceDeleted', label: 'Compliance Deleted' },
  { value: 'AssetStatusChanged', label: 'Asset Status Changed' },
] as const;

// Event type icons and colors
const EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  InspectionRecorded: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  InspectionScheduled: { icon: Calendar, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  StatusChanged: { icon: AlertCircle, color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  DueDateDeferred: { icon: Clock, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  WorkItemCreated: { icon: Plus, color: 'text-indigo-600', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30' },
  MarkedCompliant: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  MarkedNonApplicable: { icon: FileX, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-900/30' },
  EvidenceUploaded: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  EvidenceDeleted: { icon: Trash2, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
  ComplianceAssigned: { icon: User, color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
  ComplianceDeleted: { icon: Trash2, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
  AssetStatusChanged: { icon: Edit, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
};

interface ComplianceHistoryEvent {
  id: number;
  eventType: string;
  eventDate: string;
  eventDetails: string;
  performedBy: string;
  beforeValue?: string;
  afterValue?: string;
  evidenceFiles?: Array<{
    id: number;
    fileName: string;
    fileSize: number;
    uploadedDate: string;
  }>;
}

interface ComplianceHistoryPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceItem?: {
    id: number;
    assetName: string;
    regimeName: string;
  };
  events?: ComplianceHistoryEvent[];
  loading?: boolean;
  onExport?: (format: 'pdf' | 'excel') => void;
}

// Mock data for demo
const MOCK_EVENTS: ComplianceHistoryEvent[] = [
  {
    id: 1,
    eventType: 'InspectionRecorded',
    eventDate: '2024-01-15T10:30:00',
    eventDetails: 'Annual safety inspection completed. All checks passed.',
    performedBy: 'John Smith',
    afterValue: 'Pass',
    evidenceFiles: [
      { id: 1, fileName: 'inspection-report.pdf', fileSize: 245678, uploadedDate: '2024-01-15T10:30:00' },
      { id: 2, fileName: 'photos.zip', fileSize: 1234567, uploadedDate: '2024-01-15T10:35:00' },
    ],
  },
  {
    id: 2,
    eventType: 'InspectionScheduled',
    eventDate: '2024-01-10T14:20:00',
    eventDetails: 'Next inspection scheduled for January 15, 2024',
    performedBy: 'Sarah Johnson',
  },
  {
    id: 3,
    eventType: 'StatusChanged',
    eventDate: '2023-12-20T09:15:00',
    eventDetails: 'Compliance status updated',
    performedBy: 'Mike Williams',
    beforeValue: 'Overdue',
    afterValue: 'Compliant',
  },
  {
    id: 4,
    eventType: 'DueDateDeferred',
    eventDate: '2023-12-15T16:45:00',
    eventDetails: 'Due date deferred due to equipment unavailability',
    performedBy: 'Emily Brown',
    beforeValue: '2023-12-20',
    afterValue: '2024-01-15',
  },
];

export function ComplianceHistoryPanel({
  open,
  onOpenChange,
  complianceItem,
  events = MOCK_EVENTS,
  loading = false,
  onExport,
}: ComplianceHistoryPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      // Filter by event type
      if (eventTypeFilter !== 'all' && event.eventType !== eventTypeFilter) {
        return false;
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          event.eventDetails.toLowerCase().includes(query) ||
          event.performedBy.toLowerCase().includes(query) ||
          event.eventType.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [events, eventTypeFilter, searchQuery]);

  const toggleEvent = (eventId: number) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const handleDownloadFile = (fileId: number, fileName: string) => {
    // In real app, this would download the file
    console.log('Downloading file:', fileId, fileName);
    // toast.success(`Downloading ${fileName}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col bg-white dark:bg-background">
        <SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <SheetTitle className="text-lg font-semibold">Compliance History</SheetTitle>
          <SheetDescription>
            {complianceItem ? (
              <>
                View past inspections and events for <strong>{complianceItem.assetName}</strong> - {complianceItem.regimeName}
              </>
            ) : (
              'View past inspections, compliance events, and status changes'
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-4">
          {/* Filters and Search */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger className="w-[200px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Export Buttons */}
            {onExport && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExport('pdf')}
                  className="flex-1"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExport('excel')}
                  className="flex-1"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Export Excel
                </Button>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="space-y-4">
            {loading ? (
              // Loading skeletons
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-20 w-full" />
                </div>
              ))
            ) : filteredEvents.length === 0 ? (
              // Empty state
              <div className="text-center py-12">
                <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No events found</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {searchQuery || eventTypeFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'No compliance history available yet'}
                </p>
              </div>
            ) : (
              // Event list
              filteredEvents.map((event, index) => {
                const config = EVENT_CONFIG[event.eventType] || EVENT_CONFIG.StatusChanged;
                const Icon = config.icon;
                const isExpanded = expandedEvents.has(event.id);
                const hasDetails = event.beforeValue || event.afterValue || event.evidenceFiles;

                return (
                  <div key={event.id} className="relative">
                    {/* Timeline line */}
                    {index < filteredEvents.length - 1 && (
                      <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-border" />
                    )}

                    <Collapsible open={isExpanded} onOpenChange={() => toggleEvent(event.id)}>
                      <div className="flex gap-4">
                        {/* Icon */}
                        <div className={cn('flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center', config.bgColor)}>
                          <Icon className={cn('h-5 w-5', config.color)} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="bg-muted rounded-lg p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="font-medium">
                                    {event.eventType.replace(/([A-Z])/g, ' $1').trim()}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground">
                                    {format(new Date(event.eventDate), 'PPp')}
                                  </span>
                                </div>
                                <p className="text-sm mt-2">{event.eventDetails}</p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                  <User className="h-3 w-3" />
                                  <span>{event.performedBy}</span>
                                </div>
                              </div>

                              {hasDetails && (
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {isExpanded ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </Button>
                                </CollapsibleTrigger>
                              )}
                            </div>

                            {/* Expanded Details */}
                            {hasDetails && (
                              <CollapsibleContent className="mt-4 pt-4 border-t space-y-3">
                                {/* Before/After Values */}
                                {(event.beforeValue || event.afterValue) && (
                                  <div className="grid grid-cols-2 gap-4">
                                    {event.beforeValue && (
                                      <div>
                                        <p className="text-xs font-medium text-muted-foreground mb-1">Before</p>
                                        <p className="text-sm">{event.beforeValue}</p>
                                      </div>
                                    )}
                                    {event.afterValue && (
                                      <div>
                                        <p className="text-xs font-medium text-muted-foreground mb-1">After</p>
                                        <p className="text-sm">{event.afterValue}</p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Evidence Files */}
                                {event.evidenceFiles && event.evidenceFiles.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground mb-2">Evidence Files</p>
                                    <div className="space-y-2">
                                      {event.evidenceFiles.map((file) => (
                                        <div
                                          key={file.id}
                                          className="flex items-center justify-between p-2 bg-background border rounded-md"
                                        >
                                          <div className="flex items-center gap-2 min-w-0">
                                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            <div className="min-w-0">
                                              <p className="text-sm truncate">{file.fileName}</p>
                                              <p className="text-xs text-muted-foreground">
                                                {(file.fileSize / 1024).toFixed(1)} KB
                                              </p>
                                            </div>
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDownloadFile(file.id, file.fileName)}
                                          >
                                            <Download className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </CollapsibleContent>
                            )}
                          </div>
                        </div>
                      </div>
                    </Collapsible>
                  </div>
                );
              })
            )}
          </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
